//Gets sets and removes the Auth Token Retrieved from Strapi
//Stores in local storage
import { useMutation } from "@tanstack/react-query";
import Cookies from "js-cookie";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import { useMap } from "react-leaflet";
import * as satellite from "satellite.js";
import {
  AUTH_TOKEN,
  CSRF_COOKIE_NAME,
  SARFINDER_BE_API_URL,
} from "./constants";

export const getToken = () => {
  return localStorage.getItem(AUTH_TOKEN);
};

export const setToken = (token) => {
  if (token) {
    localStorage.setItem(AUTH_TOKEN, token);
  }
};

export const removeToken = () => {
  localStorage.removeItem(AUTH_TOKEN);
};

// `quiet` skips the per-file "Download started" snackbar, for a caller that
// fires several files in a row and reports them once itself.
export const handleDownload = async (
  fileUrl,
  customFileName = null,
  { quiet = false } = {},
) => {
  if (!fileUrl) {
    console.error("No file URL provided");
    return;
  }

  try {
    // Extract filename from URL if no custom name provided
    const urlParts = fileUrl.split("/");
    const fileNameWithParams = urlParts[urlParts.length - 1] || "download";
    // Remove query parameters
    const defaultFileName = fileNameWithParams.split("?")[0];
    const fileName = customFileName || defaultFileName;

    // Get file extension
    const extension = fileName.split(".").pop().toLowerCase();

    // File types that browsers will preview instead of download
    const previewableTypes = [
      "png",
      "jpg",
      "jpeg",
      "gif",
      "svg",
      "webp",
      "txt",
      "pdf",
      "html",
      "htm",
    ];
    const shouldUseFetch = previewableTypes.includes(extension);

    if (shouldUseFetch) {
      // Use fetch for previewable files to force download
      const response = await fetch(fileUrl, {
        mode: "cors",
        method: "GET",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Convert to blob
      const blob = await response.blob();

      // Create object URL
      const objectUrl = window.URL.createObjectURL(blob);

      // Create temporary link element
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      link.style.display = "none";

      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up object URL
      window.URL.revokeObjectURL(objectUrl);
    } else {
      // Use direct download for non-previewable files (TIF, ZIP, etc.)
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = fileName;
      link.style.display = "none";

      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    if (!quiet)
      enqueueSnackbar("Download started", {
        variant: "success",
      });
  } catch (error) {
    console.error("Download failed:", error);

    // If download fails, try opening in new tab as fallback
    try {
      window.open(fileUrl, "_blank");
      enqueueSnackbar("Download failed. File opened in new tab instead.", {
        variant: "info",
      });
    } catch (openError) {
      console.error("Failed to open in new tab:", openError);
      enqueueSnackbar("Download failed. Please try again.", {
        variant: "error",
      });
    }
  }
};

export const parseXmlError = (xmlString) => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "application/xml");

    const errorInfo = {
      // exceptionCode: exceptionNode.getAttribute("exceptionCode"),
      // locator: exceptionNode.getAttribute("locator"),
      exceptionText:
        xmlDoc.getElementsByTagName("ows:ExceptionText")[0].textContent,
    };

    return errorInfo;
  } catch (error) {
    console.error("Error parsing XML:", error);
    return null;
  }
};

export const formatDetails = (details) => {
  if (!details) return "";
  return Object.entries(details)
    .map(([key, value]) => `${key}: ${value.join(", ")}`)
    .join("; ");
};

// utils/helper.js - Updated version

export const useCallAPI = (key, endpoint, type = "GET") => {
  return useMutation({
    mutationKey: key,
    mutationFn: async (input) => {
      // Allow input to override endpoint for dynamic URLs
      const finalEndpoint = input?.url || endpoint;

      // Remove url from body if it exists
      const { url, ...bodyData } = input || {};

      const response = await fetch(`${SARFINDER_BE_API_URL}${finalEndpoint}`, {
        method: type,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": Cookies.get(CSRF_COOKIE_NAME),
        },
        // Only include body for non-GET/HEAD methods
        body:
          type !== "GET" && type !== "HEAD" && Object.keys(bodyData).length > 0
            ? JSON.stringify(bodyData)
            : undefined,
      });

      if (!response.ok) {
        let errorMessage = "An error occurred while processing the request";
        let errorData = {};

        try {
          errorData = await response.json();

          // Handle different error status codes
          switch (response.status) {
            case 400:
              errorMessage = `Bad Request: ${
                errorData.message || errorData.error || "Invalid input"
              }`;
              break;
            case 404:
              errorMessage = errorData.error || "Resource not found";
              break;
            case 500:
              errorMessage = errorData.error || "Server error occurred";
              break;
            default:
              errorMessage =
                errorData.error || errorData.message || errorMessage;
          }
        } catch (jsonError) {
          // If response body is not JSON or empty, use default message
          console.error("Failed to parse error response:", jsonError);
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = errorData;

        throw error;
      }

      return response.json();
    },
  });
};

export const textToColorHex = (text) => {
  // Array of pastel colors
  const pastelColors = [
    "#FFCCCC",
    "#FFCC99",
    "#FFCCFF",
    "#CCFFCC",
    "#CCFF99",
    "#CCCCFF",
    "#99CCFF",
    "#99CC99",
    "#FF9999",
    "#FF9966",
    "#FFCC66",
    "#FFCCCC",
    "#CCFF99",
    "#FF99CC",
    "#FF9966",
    "#CCFFCC",
    "#FFCC99",
    "#CCCCFF",
    "#99CCFF",
    "#99CC99",
    "#FFCCFF",
    "#FFCC66",
    "#FF99CC",
    "#FF9966",
    "#CCFFCC",
    "#FFCCCC",
    "#FFCC99",
    "#FFCCFF",
    "#CCFFCC",
    "#CCFF99",
    "#CCCCFF",
    "#99CCFF",
    "#99CC99",
    "#FF9999",
    "#FF9966",
    "#FFCC66",
    "#FFCCCC",
    "#CCFF99",
    "#FF99CC",
    "#FF9966",
    "#CCFFCC",
    "#FFCC99",
    "#CCCCFF",
    "#99CCFF",
    "#99CC99",
    "#FFCCFF",
    "#FFCC66",
    "#FF99CC",
    "#FF9966",
    "#CCFFCC",
    // Add more pastel colors as needed
  ];

  var backgroundColor = "#000000";

  if (text === "Damage Proxy Map") backgroundColor = "#ffbd59";
  else if (text === "Flood Proxy Map") backgroundColor = "#8fd2ff";
  else {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Use modulus to select a pastel color from the array
    const colorIndex = Math.abs(hash) % pastelColors.length;

    // Set the selected pastel color as the background
    backgroundColor = pastelColors[colorIndex];

    // Calculate the luminance to determine text color
  }

  const luminance =
    (0.299 * parseInt(backgroundColor.slice(1, 3), 16) +
      0.587 * parseInt(backgroundColor.slice(3, 5), 16) +
      0.114 * parseInt(backgroundColor.slice(5, 7), 16)) /
    255;

  // Set text color based on luminance
  const textColor = luminance > 0.5 ? "#000000" : "#FFFFFF";

  return { backgroundColor, color: textColor };
};

export const formatDate = (inputDate) => {
  const formattedDate = moment(inputDate).format("DD MMMM YYYY");
  return formattedDate;
};

export const capitalizeEachWord = (inputString) => {
  return inputString.replace(/\b\w/g, (match) => match.toUpperCase());
};

export const MapWithBounds = ({ bounds, polygonBoxes, reset }) => {
  const map = useMap();

  // Handle bounds directly as a prop
  useEffect(() => {
    if (bounds && Array.isArray(bounds) && bounds.length >= 2) {
      try {
        map.fitBounds(bounds);
      } catch (error) {
        console.error("Error fitting to bounds:", error);
      }
    }
  }, [map, bounds, reset]);

  // Handle polygonBoxes (converting to Leaflet compatible bounds)
  useEffect(() => {
    if (polygonBoxes && polygonBoxes.length > 0) {
      try {
        const leafletBounds = polygonBoxes
          .flat()
          .map((coord) => [coord[1], coord[0]]);
        map.fitBounds(leafletBounds);
      } catch (error) {
        console.error("Error fitting to polygon boxes:", error);
      }
    }
  }, [map, polygonBoxes, reset]);

  return null;
};

export const selectionToPolygon = (selectionArea) => {
  const { _southWest, _northEast } = selectionArea.bounds;

  return [
    [_southWest.lat, _southWest.lng], // Southwest corner [lat, lng]
    [_southWest.lat, _northEast.lng], // Southeast corner [lat, lng]
    [_northEast.lat, _northEast.lng], // Northeast corner [lat, lng]
    [_northEast.lat, _southWest.lng], // Northwest corner [lat, lng]
    [_southWest.lat, _southWest.lng], // Close the polygon
  ];
};

export const selectionToBounds = (selectionArea) => {
  if (!selectionArea?.bounds) return null;

  const { _southWest, _northEast } = selectionArea.bounds;

  // Return bounds in [lat, lng] format that Leaflet expects
  return [
    [_southWest.lat, _southWest.lng], // Southwest corner
    [_northEast.lat, _northEast.lng], // Northeast corner
  ];
};

export const calculateArea = (coords) => {
  const lat1 = coords._southWest.lat;
  const lat2 = coords._northEast.lat;
  const lon1 = coords._southWest.lng;
  const lon2 = coords._northEast.lng;

  // WGS84 parameters
  const a = 6378137.0; // Semi-major axis (equatorial radius) in meters
  const f = 1 / 298.257223563; // Flattening
  const b = a * (1 - f); // Semi-minor axis (polar radius) in meters
  const e2 = (2 - f) * f; // First eccentricity squared

  // Convert degrees to radians
  const lat1Rad = (lat1 * Math.PI) / 180;
  const lat2Rad = (lat2 * Math.PI) / 180;
  const lonDiffRad = ((lon2 - lon1) * Math.PI) / 180;

  // Compute meridional radius of curvature (M) at midpoint latitude
  const midLatRad = (lat1Rad + lat2Rad) / 2;
  const sinMidLat = Math.sin(midLatRad);
  const M = (a * (1 - e2)) / Math.pow(1 - e2 * sinMidLat * sinMidLat, 1.5);

  // Compute normal radius of curvature (N) at midpoint latitude
  const N = a / Math.sqrt(1 - e2 * sinMidLat * sinMidLat);

  // Compute differential area dA ≈ M * dLat * N * dLon
  const dLat = lat2Rad - lat1Rad;
  const areaM2 = Math.abs(M * dLat * N * lonDiffRad); // Area in square meters
  const areaKm2 = areaM2 / 1e6; // Convert to square kilometers

  return areaKm2;
};

export const formatNumber = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export function convertCoordinateAntiMeridian(coordinate) {
  if (coordinate[1] <= 0) coordinate[1] = coordinate[1] + 360;
  return coordinate;
}

// Function to check if satellite is within the bounding box
const isInBoundingBox = (lat, lon, boundingBox) => {
  const max_lat = boundingBox._northEast.lat;
  const min_lat = boundingBox._southWest.lat;
  const max_lon = boundingBox._northEast.lng;
  const min_lon = boundingBox._southWest.lng;

  return lat >= min_lat && lat <= max_lat && lon >= min_lon && lon <= max_lon;
};

// Function to find the next overpass over multiple orbital cycles.
// 'maxCycles' determines how many orbital periods will be checked.
export const findNextOverpass = (tle, boundingBox, maxCycles = 12) => {
  const satrec = satellite.twoline2satrec(tle.line1, tle.line2);
  const steps = 175;
  // Calculate the orbital period in seconds using mean motion (satrec.no is in radians per minute)
  const orbitalPeriodSeconds = ((2 * Math.PI) / satrec.no) * 60;
  // The time step within each orbital period
  const stepInterval = orbitalPeriodSeconds / steps;
  const now = new Date();

  // Loop over 'maxCycles' orbital periods
  for (let cycle = 0; cycle < maxCycles; cycle++) {
    // Loop through time steps within the current cycle
    for (let i = 0; i < steps; i++) {
      const time = new Date(
        now.getTime() +
          (cycle * orbitalPeriodSeconds + i * stepInterval) * 1000,
      );
      const posVel = satellite.propagate(satrec, time);
      if (posVel.position) {
        const gmst = satellite.gstime(time);
        const posGd = satellite.eciToGeodetic(posVel.position, gmst);
        const lat = satellite.degreesLat(posGd.latitude);
        const lon = satellite.degreesLong(posGd.longitude);

        if (isInBoundingBox(lat, lon, boundingBox)) {
          // console.log("Next overpass time:", time);
          return time;
        }
      }
    }
  }
  // If no overpass is found within the search window, return null.
  return null;
};

// Format date for display
export const formatDateDisplay = (dateString) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  } catch (error) {
    return dateString;
  }
};

export const getSatelliteId = (identifier) => {
  if (identifier.startsWith("S1A")) return "S1A";
  if (identifier.startsWith("S1C")) return "S1C";
  if (identifier.startsWith("S1D")) return "S1D";
  if (identifier.includes("ALOS-2")) return "ALOS-2";
  if (identifier.includes("ALOS")) return "ALOS";
  return identifier.substring(0, 10);
};

export const parseIdentifiers = (input) => {
  if (!input.trim()) return [];

  // Split by comma, newline, or space, then clean up each identifier
  return input
    .split(/[,\n\s]+/)
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
};

// Parses a numeric field (single values, comma/newline-separated lists, and
// "a-b" ranges) into display tokens for the search UI. Each token is
// { raw, label, valid } — `raw` is the typed text, `label` is the prettified
// form (en-dash for ranges), `valid` flags whether it will be searched.
export const parseRangeTokens = (input) => {
  if (!input || !input.trim()) return [];

  return input
    .split(/[,\n]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .map((raw) => {
      const range = raw.match(/^(\d+)\s*-\s*(\d+)$/);
      if (range) {
        return { raw, label: `${range[1]}–${range[2]}`, valid: true };
      }
      if (/^\d+$/.test(raw)) {
        return { raw, label: raw, valid: true };
      }
      return { raw, label: raw, valid: false };
    });
};

// Utility function to convert bbox to WKT POLYGON
export const bboxToWKT = (bboxN, bboxS, bboxE, bboxW) => {
  // WKT format: POLYGON((lon1 lat1, lon2 lat2, lon3 lat3, lon4 lat4, lon1 lat1))
  // Order: SW, SE, NE, NW, SW (clockwise)
  return `POLYGON((${bboxW} ${bboxS}, ${bboxE} ${bboxS}, ${bboxE} ${bboxN}, ${bboxW} ${bboxN}, ${bboxW} ${bboxS}))`;
};

// Utility function to format bbox as individual coordinates
export const formatBboxCoordinates = (bboxN, bboxS, bboxE, bboxW) => {
  return `North: ${bboxN}\nSouth: ${bboxS}\nEast: ${bboxE}\nWest: ${bboxW}`;
};

export const geometryToSimpleString = (geometry) => {
  if (!geometry || !geometry.coordinates) return "";

  try {
    if (geometry.type === "Polygon") {
      const coords = geometry.coordinates[0]; // Get outer ring
      return coords.map((coord) => `${coord[0]} ${coord[1]}`).join(";");
    }

    if (geometry.type === "MultiPolygon") {
      // Get first polygon's outer ring for simplicity
      const coords = geometry.coordinates[0][0];
      return coords.map((coord) => `${coord[0]} ${coord[1]}`).join(";");
    }

    return `${geometry.type}: ${geometry.coordinates.length} coordinates`;
  } catch (error) {
    return "Invalid geometry";
  }
};
export const createCalendarEvent = (option) => {
  // overpassSatelliteId is the unit actually flying the pass, which can differ
  // from the row's satellite now that overpass times are plan-based.
  const satelliteInfo =
    option.overpassSatelliteId ||
    option.satelliteId ||
    option.satelliteName ||
    "Unknown";
  const relOrbit = option.relativeOrbit ? `Rel ${option.relativeOrbit}` : "";

  // Title
  let title = `${satelliteInfo} Overpass`;
  if (relOrbit) title += ` - ${relOrbit}`;

  // Description
  let description = `Satellite Overpass Event\n`;
  description += `Status: ${option.confirmationStatus?.status || "Unknown"}\n`;
  if (option.satelliteId)
    description += `Satellite ID: ${option.satelliteId}\n`;
  if (option.relativeOrbit)
    description += `Relative Orbit: ${option.relativeOrbit}\n`;

  // Validate start time
  const startTimeValue = option.overpassDate;
  if (!startTimeValue) {
    console.error("createCalendarEvent: No valid start time provided", option);
    return null;
  }

  const startTime = new Date(startTimeValue);
  if (isNaN(startTime.getTime())) {
    console.error(
      "createCalendarEvent: Invalid start time value",
      startTimeValue,
    );
    return null;
  }

  const duration = option.duration || 300; // 5 minutes
  const endTime = new Date(startTime.getTime() + duration * 1000);

  return {
    title,
    description,
    location: option.location || "",
    startTime, // keep as Date
    endTime, // keep as Date
  };
};
